/**
 * Displays log output, with integrated search.
 */

import {Alert} from '@wandb/weave/components/Alert';
import {Button} from '@wandb/weave/components/Button';
import {TextField} from '@wandb/weave/components/Form/TextField';
import {Timestamp} from '@wandb/weave/components/Timestamp';
import AU from 'ansi_up';
import _ from 'lodash';
import React, {useEffect, useRef, useState} from 'react';
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  List,
  ListRowRenderer,
  OnScrollCallback,
} from 'react-virtualized';

import {LogLineNode, QueryResultProps, withQuery} from '../graphql/runLogQuery';
import {isSameDateLocal, isSameYearLocal} from '../util/date';
import {escapeRegexChars} from '../util/regex';
import {run as runUrl} from '../util/urls';
import * as S from './Log.styles';
import {LogSearchBrowser} from './LogSearchBrowser';
import {InstrumentedCenteredWaveLoader as Loader} from './utility/InstrumentedLoader';

// this is added for https://wandb.atlassian.net/browse/WB-16553
// number chosen is non-scientific, so may be able to be adjusted
const MAX_VISIBLE_LINE_LENGTH = 5000;
const minRowHeight = 16; // should match the line-height
const cache = new CellMeasurerCache({
  defaultHeight: minRowHeight,
  fixedWidth: true,
});

interface LogProps extends QueryResultProps {
  outputLogFile: string | null;
  totalLines?: number;
  showTimestamp?: boolean;
}

export const splitLine = (node: LogLineNode): string[] => {
  const linetext = node.line;
  const idxSpace = linetext.indexOf(' ');
  const timestamp = linetext.substring(0, idxSpace);
  const text = linetext.substring(idxSpace + 1);
  return [timestamp, text];
};

// Get the short display format we should use for a specific timestamp.
const getOneTimestampFormat = (timestamp: string): string => {
  const now = new Date();
  const then = new Date(timestamp);
  let format = 'MMM Do YYYY [at] hh:mm:ssa';
  if (isSameDateLocal(now, then)) {
    format = 'hh:mm:ssa'; // Today
  } else if (isSameYearLocal(now, then)) {
    format = 'MMM Do [at] hh:mm:ssa'; // This year
  }
  return format;
};

// Use the format we would use for the first line.
// If the first line is today, the last line must be too.
const getCommonTimestampFormat = (
  lines: Array<{node: LogLineNode}>
): string | undefined => {
  if (lines.length === 0) {
    return undefined;
  }
  const firstTimestamp = splitLine(lines[0].node)[0];
  return getOneTimestampFormat(firstTimestamp);
};

const REGEX_RUN_ID =
  /WANDB_PROJECT='(.*)'.*WANDB_ENTITY=([a-z0-9\-_]*).*WANDB_RUN_ID=([a-z0-9]+)/;

// Insert wrapper text at each location.
const wrapRanges = (
  text: string,
  indices: number[],
  length: number,
  start: (index: number) => string,
  end: (index: number) => string
) => {
  let result = '';
  let lastIndex = 0;
  for (const index of indices) {
    result += text.substring(lastIndex, index);
    const startText = start(index);
    const endText = end(index);
    result += startText + text.substring(index, index + length) + endText;
    lastIndex = index + length;
  }
  result += text.substring(lastIndex);
  return result;
};

const ANSI_BACKGROUND_YELLOW = '\u001b[48;5;11m';
const ANSI_BACKGROUND_ORANGE = '\u001b[48;5;220m';
const ANSI_RESET = '\u001b[0m';

// Convert the raw text of a log line to HTML.
// This handles ANSI color codes and also links run IDs to the run page.
const toHtml = (
  index: number,
  text: string,
  searchText: string,
  searchMatches: SearchMatches,
  searchActive: number
): string => {
  // We previously used a single instance of ansi_up but unterminated escape codes
  // caused rendering problems, so we now create a new instance for each line.
  const ansiUp = new AU();
  const lineMatches = searchMatches.indexed[index] ?? [];
  let activeIndex = -1;
  if (searchActive !== -1) {
    const active = searchMatches.ordered[searchActive];
    if (active.row === index) {
      activeIndex = active.col;
    }
  }
  // TODO: If the highlight starts within an existing colored section of log, the
  // reset code will incorrectly terminate the log's highlighting too. A solution
  // that can handle nested codes would be better but the effort doesn't seem justified now.
  const highlighted = wrapRanges(
    text,
    lineMatches,
    searchText.length,
    idx =>
      idx === activeIndex ? ANSI_BACKGROUND_ORANGE : ANSI_BACKGROUND_YELLOW,
    () => ANSI_RESET
  );

  const match = REGEX_RUN_ID.exec(highlighted);
  if (match == null) {
    return ansiUp.ansi_to_html(highlighted.replace(/ /g, '\u00a0'));
  }
  const [, projectName, entityName, name] = match;
  const urlParams = {entityName, projectName, name};
  const urlRun = runUrl(urlParams);
  const link = `<a href="${urlRun}">${name}</a>`;
  const parts = highlighted
    .split(name)
    .map(part => ansiUp.ansi_to_html(part.replace(/ /g, '\u00a0')));
  return parts.join(link);
};

// Starting position of a match. We don't support matching across lines or match patterns.
type SearchMatch = {
  row: number;
  col: number;
};

type SearchMatches = {
  // Helps us go to previous / next match and quickly get count.
  ordered: SearchMatch[];
  // Helps us with highlighting.
  indexed: Record<number, number[]>;
};

const LogComp = ({
  runLogQuery,
  totalLines,
  outputLogFile,
  showTimestamp,
}: LogProps) => {
  const [autoScroll, setAutoScroll] = useState(true);
  const [lines, setLines] = useState<Array<{node: LogLineNode}>>([]);

  // Match the user is viewing
  const [searchActive, setSearchActive] = useState(-1);
  const [searchMatches, setSearchMatches] = useState<SearchMatches>({
    ordered: [],
    indexed: {},
  });

  const [searchText, setSearchText] = useState('');

  const lastWidthRef = useRef(window.innerWidth);

  const listRef = useRef<List>(null);

  const timestampFormat = showTimestamp
    ? getCommonTimestampFormat(lines)
    : undefined;

  useEffect(() => {
    scrollToBottom();
  });

  const scrollTo = (row: number) => {
    const list = listRef.current;
    if (list) {
      list.forceUpdateGrid();
      list.scrollToRow(row);
    }
  };

  const scrollToBottom = () => {
    const list = listRef.current;
    if (list) {
      list.forceUpdateGrid();
    }
    const lineCount = lines.length;
    if (lineCount !== 0 && autoScroll) {
      if (list) {
        list.scrollToRow(lineCount);
      }
    }
  };

  const checkAutoScroll: OnScrollCallback = e => {
    // autoScroll is enabled if current scroll position is close to the bottom
    // or if list is still empty meaning List height is zero
    const scroll = e.scrollHeight - e.clientHeight - Math.round(e.scrollTop);
    setAutoScroll(scroll < 5 || e.clientHeight === 0);
  };

  const onSearchChange = (value: string) => {
    const matches: SearchMatches = {
      ordered: [],
      indexed: {},
    };
    if (value) {
      const regex = new RegExp(escapeRegexChars(value), 'ig');
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const [, text] = splitLine(line.node);
        for (const match of text.matchAll(regex)) {
          const {index} = match;
          if (index != null) {
            matches.ordered.push({row: i, col: index});
            if (i in matches.indexed) {
              matches.indexed[i].push(index);
            } else {
              matches.indexed[i] = [index];
            }
          }
        }
      }
    }
    setSearchText(value);
    setSearchMatches(matches);
    setSearchActive(matches.ordered.length ? 0 : -1);
    if (matches.ordered.length) {
      scrollTo(matches.ordered[0].row);
    }
  };

  // When a new set of lines has loaded, we need to poke the search state.
  useEffect(() => {
    if (!runLogQuery.loading) {
      setLines(runLogQuery.logLines.edges);
      cache.clearAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runLogQuery.loading, runLogQuery.logLines]);

  useEffect(() => {
    if (searchText) {
      onSearchChange(searchText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines]);

  const onSetIndex = (index: number) => {
    setSearchActive(index);
    scrollTo(searchMatches.ordered[index].row);
  };

  const onKeyDown = (key: string) => {
    if (key === 'Enter' && searchMatches.ordered.length) {
      onSetIndex((searchActive + 1) % searchMatches.ordered.length);
    }
  };

  const rowRenderer: ListRowRenderer = ({key, index, style, parent}) => {
    // Only called on valid keys, since we pass rowCount in to VirtualList
    const line = lines[index];
    const [timestamp, rawText] = splitLine(line.node);

    const text =
      rawText.length > MAX_VISIBLE_LINE_LENGTH
        ? rawText.substring(0, MAX_VISIBLE_LINE_LENGTH) +
          '[wandb: long log line truncated]'
        : rawText;
    return (
      <CellMeasurer
        cache={cache}
        columnIndex={0}
        key={key}
        parent={parent}
        rowIndex={index}
        minHeight={minRowHeight}>
        <div
          role="row"
          style={{
            ...style,
            overflowWrap: 'break-word',
          }}>
          {totalLines != null && (
            <S.LineNumber>
              {`${index + 1 + Math.max(totalLines - lines.length, 0)}`.padStart(
                `${lines.length}`.length,
                '\u00a0'
              )}
            </S.LineNumber>
          )}
          {showTimestamp && (
            <>
              {'\u00a0'}
              <S.Timestamp>
                <Timestamp value={timestamp} format={timestampFormat} />
              </S.Timestamp>
            </>
          )}
          {'\u00a0'}
          <span
            dangerouslySetInnerHTML={{
              __html:
                // The ansi to HTML library takes care of escaping.
                // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
                toHtml(index, text, searchText, searchMatches, searchActive) +
                '<br />', // ensures that empty lines have a height
            }}
          />
        </div>
      </CellMeasurer>
    );
  };

  if (runLogQuery.loading) {
    return <Loader name="log-loader" size="huge" />;
  }

  const rowCount = lines.length;
  // pass through lastLine to force update when it changes (progress bars)
  let lastLine = '';
  if (rowCount > 0) {
    lastLine = _.last(lines)!.node.line;
  }

  // Because of useEffect, the query might be finished before "lines" is set.
  // To avoid a flashing appearance of this alert, we check for queryRowCount instead.
  const queryRowCount = runLogQuery.logLines.edges.length;
  if (queryRowCount === 0) {
    // Agents didn't used to save logs to a run. Runs might not have output.
    return (
      <div style={{margin: 10}}>
        <Alert severity="info">No log output available.</Alert>
      </div>
    );
  }

  return (
    <S.LogContainer>
      <S.LogHeader>
        <S.Search>
          <TextField
            icon="search"
            placeholder="Search logs"
            value={searchText}
            onChange={onSearchChange}
            onKeyDown={onKeyDown}
          />
        </S.Search>
        {searchText && (
          <LogSearchBrowser
            index={searchActive}
            count={searchMatches.ordered.length}
            onSetIndex={onSetIndex}
          />
        )}
        {totalLines != null && lines.length < totalLines && (
          <S.Truncated>
            Showing most recent {lines.length.toLocaleString()} out of{' '}
            {totalLines.toLocaleString()} lines.
          </S.Truncated>
        )}
        <S.Aligner />
        {outputLogFile && (
          <a href={outputLogFile} download="output.log">
            <Button variant="secondary" size="large">
              Download
            </Button>
          </a>
        )}
      </S.LogHeader>
      <S.LogText>
        <AutoSizer>
          {({width, height}) => {
            if (width !== lastWidthRef.current) {
              // recompute row heights on window resize
              cache.clearAll();
              lastWidthRef.current = width;
            }
            return (
              <List
                ref={listRef}
                onScroll={checkAutoScroll}
                height={height}
                width={width}
                rowCount={rowCount}
                estimatedRowHeight={minRowHeight}
                rowHeight={cache.rowHeight}
                rowRenderer={rowRenderer}
                lastLine={lastLine}
              />
            );
          }}
        </AutoSizer>
      </S.LogText>
    </S.LogContainer>
  );
};

export const Log = withQuery(LogComp);
